import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const SORTS = [
  { v: "newest", l: "Mais recentes" },
  { v: "price_asc", l: "Preço: mais baixo" },
  { v: "price_desc", l: "Preço: mais alto" },
  { v: "name_asc", l: "Nome A-Z" },
];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "Todos";
  const subcategory = params.get("subcategory") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "newest";

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category !== "Todos") p.category = category;
    if (subcategory) p.subcategory = subcategory;
    if (q) p.q = q;
    if (sort) p.sort = sort;
    api.get("/products", { params: p }).then(({ data }) => {
      setProducts(data);
      setLoading(false);
    });
  }, [category, subcategory, q, sort]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v && v !== "Todos") next.set(k, v); else next.delete(k);
    // Clear subcategory when category changes
    if (k === "category") next.delete("subcategory");
    setParams(next);
  };

  const allCats = [{ slug: "Todos", name: "Todos" }, ...categories];
  const currentCat = categories.find((c) => c.slug === category);
  const subs = currentCat?.subcategories || [];

  const heading = q
    ? `Resultados para "${q}"`
    : subcategory
    ? `${category} · ${subs.find((s) => s.slug === subcategory)?.name || subcategory}`
    : category === "Todos"
    ? "Todas as peças"
    : category;

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-12 py-16" data-testid="page-catalog">
      <div className="mb-12">
        <p className="label-sm mb-3">Loja</p>
        <h1 className="font-serif text-5xl lg:text-6xl brand-text">{heading}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-12">
        {/* Sidebar filters */}
        <aside className="space-y-8">
          <div>
            <p className="label-sm mb-4">Categoria</p>
            <ul className="space-y-2">
              {allCats.map((c) => (
                <li key={c.slug}>
                  <button
                    onClick={() => setParam("category", c.slug)}
                    className={`text-sm transition ${category === c.slug ? "brand-primary-text font-medium" : "brand-text hover:brand-primary-text"}`}
                    data-testid={`filter-cat-${c.slug}`}
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {subs.length > 0 && (
            <div data-testid="filter-subs">
              <p className="label-sm mb-4">Subcategorias</p>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => setParam("subcategory", "")}
                    className={`text-sm transition ${!subcategory ? "brand-primary-text font-medium" : "brand-text hover:brand-primary-text"}`}
                    data-testid="filter-sub-all"
                  >
                    Todas
                  </button>
                </li>
                {subs.map((s) => (
                  <li key={s.slug}>
                    <button
                      onClick={() => setParam("subcategory", s.slug)}
                      className={`text-sm transition ${subcategory === s.slug ? "brand-primary-text font-medium" : "brand-text hover:brand-primary-text"}`}
                      data-testid={`filter-sub-${s.slug}`}
                    >
                      {s.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <p className="label-sm mb-4">Ordenar por</p>
            <select
              value={sort}
              onChange={(e) => setParam("sort", e.target.value)}
              className="input-line"
              data-testid="filter-sort"
            >
              {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>

          {q && (
            <button onClick={() => setParam("q", "")} className="text-sm brand-primary-text underline" data-testid="clear-search">
              Limpar pesquisa
            </button>
          )}
        </aside>

        {/* Products */}
        <div>
          {loading ? (
            <p className="brand-muted">A carregar...</p>
          ) : products.length === 0 ? (
            <p className="brand-muted py-20 text-center">Não foram encontrados produtos.</p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10 stagger" data-testid="catalog-grid">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
